import Decimal from "decimal.js";
import commonBaseService from "../../commonBaseService";
import orderScripService from "../orderForm/orderScripService";

function calculateInvestedValue(quantity, average, lot_size, instrument) {
	if (commonBaseService.isEmptyOrUndefined(quantity) || commonBaseService.isEmptyOrUndefined(average)) {
		return '-';
	}
	const isCurrencyScrip = orderScripService.isCurrencyDerivatesScrip(instrument);
	const quantityAmount = Number(quantity);
	const averageAmount = Number(average);
	if (!commonBaseService.isValidNumber(quantityAmount) || !commonBaseService.isValidNumber(averageAmount)) {
		return '-';
	}
	let investedValue = quantityAmount * averageAmount;
	if (isCurrencyScrip) {
		investedValue *= lot_size;
	}
	return investedValue;
}

function calculateCurrentValue(quantity, ltp) {
	if (commonBaseService.isEmptyOrUndefined(quantity) || commonBaseService.isEmptyOrUndefined(ltp)) {
		return '-';
	}
	const quantityAmount = Number(quantity);
	const averageAmount = Number(ltp);
	if (!commonBaseService.isValidNumber(quantityAmount) || !commonBaseService.isValidNumber(averageAmount)) {
		return '-';
	}
	const currentValue = quantityAmount * averageAmount;
	return currentValue;
}

function calculateOverAllPnl(current, invested) {
	if (commonBaseService.isEmptyOrUndefined(current) || commonBaseService.isEmptyOrUndefined(invested)) {
		return '-';
	} else {
		const currentValue = Number(current);
		const investedValue = Number(invested);
		if (commonBaseService.isValidNumber(currentValue) && commonBaseService.isValidNumber(investedValue)) {
			return currentValue - investedValue;
		}
		return '-';
	}
}

function calculateOverAllPnlPercentage(current, invested) {
	if (commonBaseService.isEmptyOrUndefined(current) || commonBaseService.isEmptyOrUndefined(invested)) {
		return '-';
	} else {
		const currentValue = Number(current);
		const investedValue = Number(invested);
		if (!commonBaseService.isValidNumber(currentValue) && !commonBaseService.isValidNumber(investedValue)) {
			return '-';
		}
		const totalChange = currentValue - investedValue;
		const overAllPnlPercentage = (totalChange / investedValue) * 100;
		if (commonBaseService.isValidNumber(overAllPnlPercentage)) {
			return overAllPnlPercentage;
		} else {
			return '-';
		}
	}
}


function calculateTodaysPnl(quantity, oneDayChange) {
	if (commonBaseService.isEmptyOrUndefined(quantity) || commonBaseService.isEmptyOrUndefined(oneDayChange)) {
		return '-';
	}
	const quantityNum = Number(quantity);
	const oneDayChangeNum = Number(oneDayChange);

	if (!commonBaseService.isValidNumber(quantityNum) || !commonBaseService.isValidNumber(oneDayChangeNum)) {
		return '-';
	} else {
		const todaysPnl = quantity * oneDayChangeNum;
		return todaysPnl;
	}
}

function calculateTodaysPnlPerc(todaysPnl, currentValue) {
	if (commonBaseService.isEmptyOrUndefined(todaysPnl) || commonBaseService.isEmptyOrUndefined(currentValue)) {
		return '-';
	}
	const todaysPnlAmount = Number(todaysPnl);
	const currentValueAmount = Number(currentValue);
	if (!commonBaseService.isValidNumber(todaysPnlAmount) || !commonBaseService.isValidNumber(currentValueAmount)) {
		return '-';
	} else {
		const lastDayInvestedAmount = currentValueAmount - todaysPnlAmount;
		const todaysPnlPerc = (todaysPnlAmount / lastDayInvestedAmount) * 100;
		if (commonBaseService.isValidNumber(todaysPnlPerc)) {
			return todaysPnlPerc;
		}

		return '-';
	}
}

const getHoldingSummaryData = (data, searchInput = '') => {
	let invested = 0;
	let current = 0;
	let todaysPnl = 0;
	let totalPnl = 0;
	let totalPnlPercentage = 0;
	let totalTodaysPnlPerc = 0;

	data.forEach((value) => {
		if (searchInput === '' || value.display_name?.toUpperCase().includes(searchInput.toUpperCase())) {
			invested += value._invested;
			current += value._current;
			totalPnl += value._overallPnl;
			todaysPnl += value._todaysPnl;
		}
	});

	totalPnlPercentage = calculateOverAllPnlPercentage(current, invested);
	totalTodaysPnlPerc = calculateTodaysPnlPerc(todaysPnl, current);
	return { invested, current, totalPnl, todaysPnl, totalPnlPercentage, totalTodaysPnlPerc };
};

const getPositionsSummaryData = (data, searchInput = '') => {
	let totalOverallPnl = 0;

	data.forEach((item) => {
		const displayNameUpperCase = item.display_name?.toUpperCase();

		if (displayNameUpperCase?.includes(searchInput)) {
			totalOverallPnl += item._overallPnl;
		}
	});

	return { totalOverallPnl };
};

const calculatePositionsOverallPnl = (
	cf_quantity,
	cf_avg_price,
	buy_quantity,
	avg_buy_price,
	sell_quantity,
	avg_sell_price,
	ltp,
	lot_size,
	isCurrency,
) => {
	const buy = Decimal.mul(buy_quantity, Decimal.sub(ltp, avg_buy_price));
	const sell = Decimal.mul(sell_quantity, Decimal.sub(ltp, avg_sell_price));
	let result = 0;

	if (cf_quantity) {
		const cfValue = Decimal.mul(cf_quantity, Decimal.sub(ltp, cf_avg_price));
		result = Decimal.add(cfValue, Decimal.sub(buy, sell));
	} else {
		result = Decimal.sub(buy, sell);
	}
	if (isCurrency) {
		result *= lot_size;
	}

	if (commonBaseService.isValidNumber(result)) {
		return commonBaseService.truncateNumber(result, 6);
	} else {
		return '-';
	}
};

const calculatePositionsTodaysPnl = (
	cf_quantity,
	buy_quantity,
	avg_buy_price,
	sell_quantity,
	avg_sell_price,
	ltp,
	one_day_change,
	lot_size,
	isCurrency,
) => {
	const buy = Decimal.mul(buy_quantity, Decimal.sub(ltp, avg_buy_price));
	const sell = Decimal.mul(sell_quantity, Decimal.sub(ltp, avg_sell_price));

	let result = 0;

	if (cf_quantity) {
		const cf = Decimal.mul(cf_quantity, one_day_change);
		result = Decimal.add(cf, Decimal.sub(buy, sell));
	} else {
		result = Decimal.sub(buy, sell);
	}

	if (isCurrency) {
		result *= lot_size;
	}

	if (commonBaseService.isValidNumber(result)) {
		return commonBaseService.truncateNumber(result, 6);
	}
	return '-';
};

const calculateHoldingPnlValues = (params = {}) => {
	const { quantity, last_trade_price, averagePrice, one_day_change } = params;
	const currentValue = calculateCurrentValue(quantity, last_trade_price);
	const investedValue = calculateInvestedValue(quantity, averagePrice);
	const overallPnl = calculateOverAllPnl(currentValue, investedValue);
	const overallPnlPercentage = calculateOverAllPnlPercentage(currentValue, investedValue);
	const todaysPnl = calculateTodaysPnl(quantity, one_day_change);
	return { currentValue, investedValue, overallPnl, overallPnlPercentage, todaysPnl };
};

const calculatePositionPnlValues = (params) => {
	const {
		last_trade_price,
		avg_buy_price,
		avg_sell_price,
		cf_quantity,
		cf_avg_price,
		buy_quantity,
		sell_quantity,
		one_day_change,
		lot_size,
		isCurrencyScrip,
	} = params;

	const lastTradePrice = new Decimal(parseFloat(last_trade_price));
	const oneDayChange = new Decimal(parseFloat(one_day_change));
	const cfAvgPrice = new Decimal(parseFloat(cf_avg_price));
	const avgBuyPrice = new Decimal(parseFloat(avg_buy_price));
	const avgSellPrice = new Decimal(parseFloat(avg_sell_price));

	const overallPnl = stocksPortfolioPnlService.calculatePositionsOverallPnl(
		cf_quantity,
		cfAvgPrice,
		buy_quantity,
		avgBuyPrice,
		sell_quantity,
		avgSellPrice,
		lastTradePrice,
		lot_size,
		isCurrencyScrip,
	);
	const todaysPnl = stocksPortfolioPnlService.calculatePositionsTodaysPnl(
		cf_quantity,
		buy_quantity,
		avgBuyPrice,
		sell_quantity,
		avgSellPrice,
		lastTradePrice,
		oneDayChange,
		lot_size,
		isCurrencyScrip,
	);

	return { overallPnl, todaysPnl };
};

const stocksPortfolioPnlService = {
	calculateInvestedValue,
	getHoldingSummaryData,
	calculatePositionsOverallPnl,
	calculatePositionsTodaysPnl,
	getPositionsSummaryData,
	calculateHoldingPnlValues,
	calculateCurrentValue,
	calculateTodaysPnl,
	calculateOverAllPnl,
	calculateOverAllPnlPercentage,
	calculatePositionPnlValues
}

export default stocksPortfolioPnlService